<template><div><code-well-header class="d-d-block">
  <example-select-menu label="Label" />
</code-well-header>
<!-- <component-combinator component-name="DtSelectMenu" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">DtSelectMenu is bound to a value using <code v-pre>v-model</code>, which emits the selected option's value. To wire DtSelectMenu to a reactive store (such as Vuex or Pinia), bind <code v-pre>:modelValue</code> to the store getter and listen for <code v-pre>@update:modelValue</code> to commit the mutation — or use <code v-pre>v-model</code> with a computed setter.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use sparingly — only when a user needs to choose from about seven to 15 possible options, and you have limited space to display the options.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">For site navigation.</li>
<li class="d-docsite--list-element">If the list of options is very short. Use <RouteLink to="/components/radio.html" class="d-docsite--link d-link">Radio</RouteLink> instead.</li>
<li class="d-docsite--list-element">If the list of options is very long. Let users type the same information into an <RouteLink to="/components/input.html" class="d-docsite--link d-link">Input</RouteLink> that suggests possible options instead (aka Combobox).</li>
<li class="d-docsite--list-element">Avoid using the <code v-pre>multiple</code> attribute. Users often don’t understand how to select multiple items from the select element (e.g. by holding down a modifier key).</li>
<li class="d-docsite--list-element">For selecting an action that takes immediate effect. A <code v-pre>select</code> is for selecting a choice that is only confirmed by a separate submit action (much like a <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink>). For immediate actions consider the <RouteLink to="/components/dropdown.html" class="d-docsite--link d-link">Dropdown</RouteLink> component.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Selects should be considered the “UI of last resort,” as users often find them confusing and difficult to use. Consider testing thoroughly with members of your target audience.</li>
<li class="d-docsite--list-element">Avoid making options in one dropdown menu change based on the input to another. Users often don’t understand how selecting an item in one impacts another.</li>
<li class="d-docsite--list-element">When most users will (or should) pick a particular option, make it the default selection.</li>
<li class="d-docsite--list-element">Avoid auto-submission. Be wary of UI implications of automatically submitting upon selection or applying its value. Users may often change their choices multiple times, particularly if interacting with a form solely with keyboard. Auto-submission is also less accessible. For auto-submission consider the <RouteLink to="/components/dropdown.html" class="d-docsite--link d-link">Dropdown</RouteLink> component.</li>
</ul>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="base" tabindex="-1" class="d-docsite--header-3">Base <a class="header-anchor d-link d-docsite--link d-link" href="#base" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">A select is normally paired with a label, but there are times when it can be used without a label. Don't rely on the placeholder text as a label.</p>
<code-well-header bgclass="d-bgc-primary" class="d-d-block">
  <div class="d-stack16 d-w100p">
    <example-select-menu label="Default" />
    <example-select-menu label="Disabled" disabled />
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label>
    <div class="d-select">
      <select class="d-select__input">
        <option value=""> Please select one </option>
        <option value="1"> Option 1 </option>
        <option value="2"> Option 2 </option>
        <option value="3"> Option 3 </option>
      </select>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label">Disabled</div>
    <div class="d-select d-select--disabled">
      <select disabled="disabled" class="d-select__input">
        <option class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
</div>
'
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-select-menu
  :options="[
        { value: ``, label: `Please select one` },
        { value: `1`, label: `Option 1` },
        { value: `2`, label: `Option 2` },
        { value: `3`, label: `Option 3` },
      ]"
  label="Default"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
<dt-select-menu
  :options="[
        { value: ``, label: `Please select one` },
        { value: `1`, label: `Option 1` },
        { value: `2`, label: `Option 2` },
        { value: `3`, label: `Option 3` },
      ]"
  label="Disabled"
  disabled
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
'
showHtmlWarning />
<h3 id="with-description-text" tabindex="-1" class="d-docsite--header-3">With Description Text <a class="header-anchor d-link d-docsite--link d-link" href="#with-description-text" aria-hidden="true">#</a></h3>
<code-well-header bgclass="d-bgc-primary" class="d-d-block">
  <example-select-menu label="Label" description="Optional description text" />
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label>
    <div aria-details="select-dt0-description" class="d-label">Label</div>
    <div id="select-dt0-description" class="d-description">Optional description text</div>
    <div class="d-select">
      <select class="d-select__input">
        <option class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
</div>
'
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-select-menu
  :options="[
        { value: ``, label: `Please select one` },
        { value: `1`, label: `Option 1` },
        { value: `2`, label: `Option 2` },
        { value: `3`, label: `Option 3` },
      ]"
  label="Label"
  description="Optional description text"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
'
showHtmlWarning />
<h3 id="with-validation-states" tabindex="-1" class="d-docsite--header-3">With Validation States <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provides feedback to the user based on their interaction, or lack thereof, with a select.</p>
<code-well-header bgclass="d-bgc-primary" class="d-d-block">
  <div class="d-stack16 d-w100p">
    <example-select-menu
      label="Label"
      :messages='[{"message":"Error validation message","type":"error"}]'
    />
    <example-select-menu
      label="Label"
      :messages='[{"message":"Success validation message","type":"success"}]'
    />
    <example-select-menu
      label="Label"
      :messages='[{"message":"Warning validation message","type":"warning"}]'
    />
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label>
    <div class="d-label">Label</div>
    <div class="d-select">
      <select class="d-select__input d-select__input--error">
        <option class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
  <div class="base-input__messages d-validation-message__container">
    <div role="status" aria-live="polite" class="base-input__message d-validation-message base-input__message--error d-validation-message--error"><p>Error validation message</p></div>
  </div>
</div>
<div>
  <label>
    <div class="d-label">Label</div>
    <div class="d-select">
      <select class="d-select__input d-select__input--success">
        <option class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
  <div class="base-input__messages d-validation-message__container">
    <div role="status" aria-live="polite" class="base-input__message d-validation-message base-input__message--success d-validation-message--success"><p>Success validation message</p></div>
  </div>
</div>
<div>
  <label>
    <div class="d-label">Label</div>
    <div class="d-select">
      <select class="d-select__input d-select__input--warning">
        <option class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
  <div class="base-input__messages d-validation-message__container">
    <div role="status" aria-live="polite" class="base-input__message d-validation-message base-input__message--warning d-validation-message--warning"><p>Warning validation message</p></div>
  </div>
</div>
'
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  :messages="[{ message: `Error validation message`, type: `error` }]"
  label="Label"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  :messages="[{ message: `Success validation message`, type: `success` }]"
  label="Label"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  :messages="[{ message: `Warning validation message`, type: `warning` }]"
  label="Label"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
'
showHtmlWarning />
<h3 id="with-validation-states-hidden" tabindex="-1" class="d-docsite--header-3">With Validation States Hidden <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states-hidden" aria-hidden="true">#</a></h3>
<code-well-header bgclass="d-bgc-primary" class="d-d-block">
  <div class="d-stack16 d-w100p" ref="messagesHidden">
    <example-select-menu
      label="Label"
      :messages='[{"message":"Error validation message","type":"error"}]'
      :show-messages="false"
    />
    <example-select-menu
      label="Label"
      :messages='[{"message":"Success validation message","type":"success"}]'
      :show-messages="false"
    />
    <example-select-menu
      label="Label"
      :messages='[{"message":"Warning validation message","type":"warning"}]'
      :show-messages="false"
    />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.messagesHidden"
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  :messages="[{ message: `Error validation message`, type: `error` }]"
  label="Label"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
  :show-messages="false"
/>
<dt-select-menu
  :options="[
    { value:``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  :messages="[{ message: `Success validation message`, type: `success` }]"
  label="Label"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
  :show-messages="false"
/>
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  :messages="[{ message: `Warning validation message`, type: `warning` }]"
  label="Label"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
  :show-messages="false"
/>
'
/>
<h3 id="with-slotted-label" tabindex="-1" class="d-docsite--header-3">With Slotted Label <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-label" aria-hidden="true">#</a></h3>
<code-well-header class="d-d-block">
  <example-select-menu ref="slottedLabel">
    <template #label>
      <div>Slotted label</div>
    </template>
  </example-select-menu>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.slottedLabel"
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
>
  <template #label>
    <div>Slotted label</div>
  </template>
</dt-select-menu>
'
/>
<h3 id="with-slotted-description" tabindex="-1" class="d-docsite--header-3">With Slotted Description <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-description" aria-hidden="true">#</a></h3>
<code-well-header class="d-d-block">
  <example-select-menu ref="slottedDescription">
    <template #description>
      <div>Slotted description</div>
    </template>
  </example-select-menu>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.slottedDescription"
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
>
  <template #description>
    <div>Slotted description</div>
  </template>
</dt-select-menu>
'
/>
<h3 id="with-slotted-options" tabindex="-1" class="d-docsite--header-3">With Slotted Options <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-options" aria-hidden="true">#</a></h3>
<code-well-header class="d-d-block">
  <dt-select-menu
    label="With Slotted Options"
    ref="slottedOptions"
  >
    <option value="">Slotted options</option>
    <option value="1">Option 1</option>
    <option value="2">Option 2</option>
    <option value="3">Option 3</option>
  </dt-select-menu>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.slottedOptions"
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-select-menu
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
>
  <template #default>
    <option
      v-for="option in options"
      :key="`with-slotted-options-${option.value}`"
      :value="option.value"
    >
      {{ option.label }}
    </option>
  </template>
</dt-select-menu>
'
/>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We offer different sizes for instances in which the interface requires a smaller or larger select. In general, though, use the base (medium) size select as much as possible, especially in forms.</p>
<code-well-header bgclass="d-bgc-primary" class="d-d-block">
  <div class="d-stack16 d-w100p">
    <example-select-menu label="Label" size="xs" />
    <example-select-menu label="Label" size="sm" />
    <example-select-menu label="Label" size="md" />
    <example-select-menu label="Label" size="lg" />
    <example-select-menu label="Label" size="xl" />
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label>
    <div class="d-label d-label--xs">Label</div>
    <div class="d-select d-select--xs">
      <select class="d-select__input">
        <option class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--sm">Label</div>
    <div class="d-select d-select--sm">
      <select class="d-select__input">
        <option class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--md">Label</div>
    <div class="d-select d-select--md">
      <select class="d-select__input">
        <option class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--lg">Label</div>
    <div class="d-select d-select--lg">
      <select class="d-select__input">
        <option class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--xl">Label</div>
    <div class="d-select d-select--xl">
      <select class="" value=""> Please select one </option>
        <option class="" value="1"> Option 1 </option>
        <option class="" value="2"> Option 2 </option>
        <option class="" value="3"> Option 3 </option>
      </select>
    </div>
  </label>
</div>
'
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  label="Label"
  size="xs"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  label="Label"
  size="sm"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  label="Label"
  size="md"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  label="Label"
  size="lg"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
<dt-select-menu
  :options="[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]"
  label="Label"
  size="xl"
  :model-value="modelValue"
  @input="onInput"
  @change="onChange"
/>
'
showHtmlWarning />
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Make sure the <code v-pre>label</code> <code v-pre>for</code> attribute match the select <code v-pre>id</code>.</li>
<li class="d-docsite--list-element">Avoiding removing <code v-pre>labels</code>. Labelled selects are user-friendly.</li>
<li class="d-docsite--list-element">Avoid relying on placeholder text as a substitute for a label.</li>
<li class="d-docsite--list-element">Avoid customizing the placeholder text.</li>
<li class="d-docsite--list-element">If the select is a required field, use the <code v-pre>aria-required</code> property and use the validation message for input errors.</li>
<li class="d-docsite--list-element">Select with validation errors should have <code v-pre>aria-describedby</code> with the <code v-pre>id</code> of the validation message.</li>
</ul>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="selectmenu" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="select" /></div></template>


<script setup>
  import ExampleSelectMenu from '@exampleComponents/ExampleSelectMenu.vue';
</script>